<template>
  <div class="register-page">
    <div class="register-shell">
      <div class="register-header">
        <p class="eyebrow">Get Started</p>
        <h1>{{ headerCopy.title }}</h1>
        <p>{{ headerCopy.subtitle }}</p>
      </div>

      <form v-if="step === 'phone'" class="register-card" @submit.prevent="sendSignupCode">
        <p class="form-detail">
          First, verify your phone number. Then we will finish your account setup.
        </p>

        <div class="form-group">
          <label for="phone" class="form-label">Phone Number</label>
          <div class="phone-input-wrap" :class="{ error: errors.phone }">
            <span class="country-prefix" aria-hidden="true">+1</span>
            <input
              id="phone"
              v-model="form.phone"
              type="tel"
              inputmode="numeric"
              autocomplete="tel-national"
              class="phone-input"
              placeholder="555 123 4567"
              maxlength="14"
              required
              aria-describedby="phone-help"
              @input="formatPhoneInput"
            />
          </div>
          <p id="phone-help" class="form-help">Paulie is currently available only in the U.S. and Canada.</p>
          <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
          <p v-if="existingAccountDetected" class="account-exists-message">
            An account already exists for this phone number.
            <router-link class="link" :to="{ path: '/login', query: phoneQuery }">Login instead</router-link>.
          </p>
        </div>

        <button type="submit" class="btn btn-primary w-full" :disabled="loading">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Sending Code...' : 'Send Code' }}
        </button>

        <p class="register-footer">
          Already have an account?
          <router-link class="link" :to="{ path: '/login', query: phoneQuery }">Login</router-link>
        </p>
      </form>

      <form v-else-if="step === 'code'" class="register-card" @submit.prevent="verifySignupCode">
        <div class="code-header">
          <p class="eyebrow">Verify Phone</p>
          <h2>Enter The Code</h2>
          <p>We sent a one-time code to {{ maskedPhone }}.</p>
        </div>

        <div class="form-group">
          <label for="code" class="form-label">Verification Code</label>
          <input
            id="code"
            v-model="form.code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            class="form-input code-input"
            :class="{ error: errors.code }"
            maxlength="8"
            placeholder="123456"
            required
          />
          <span v-if="errors.code" class="error-message">{{ errors.code }}</span>
        </div>

        <div class="register-actions">
          <button type="submit" class="btn btn-primary" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            {{ loading ? 'Verifying...' : 'Verify' }}
          </button>
          <button type="button" class="btn btn-outline" :disabled="loading" @click="step = 'phone'">
            Back
          </button>
        </div>
      </form>

      <form v-else class="register-card" @submit.prevent="completeRegistration">
        <p class="form-detail">
          Your username is how your account will appear inside Paulie.
        </p>

        <div class="form-group">
          <label for="username" class="form-label">Username</label>
          <input
            id="username"
            v-model="form.username"
            type="text"
            class="form-input"
            :class="{ error: errors.username }"
            placeholder="Choose a username"
            autocomplete="username"
            maxlength="24"
            required
            @input="formatUsername"
          />
          <p class="form-help">Use 3-24 letters, numbers, or underscores.</p>
          <span v-if="errors.username" class="error-message">{{ errors.username }}</span>
        </div>

        <p class="register-toggle-note">
          {{ showJoinCode ? 'No join code?' : 'Have a join code?' }}
          <button type="button" class="inline-toggle" @click="toggleJoinCode">
            {{ showJoinCode ? 'Remove it' : 'Add it' }}
          </button>
        </p>

        <div v-if="showJoinCode" class="form-group join-code-panel">
          <label for="joinCode" class="form-label">Join Code <span>Optional</span></label>
          <input
            id="joinCode"
            v-model="form.joinCode"
            type="text"
            class="form-input"
            :class="{ error: errors.joinCode }"
            placeholder="Enter code"
            autocomplete="off"
            maxlength="32"
            @input="formatJoinCode"
          />
          <span v-if="errors.joinCode" class="error-message">{{ errors.joinCode }}</span>
        </div>

        <button type="submit" class="btn btn-primary w-full" :disabled="loading">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Creating Account...' : 'Create Account' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { auth, hasSupabaseEnv, userProfiles } from '@/lib/supabase'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const step = ref('phone')
const showJoinCode = ref(Boolean(route.query.join_code))
const verifiedUser = ref(null)
const existingAccountDetected = ref(false)
const errors = reactive({})

const form = reactive({
  phone: formatPhoneFromQuery(route.query.phone),
  username: '',
  joinCode: String(route.query.join_code || '').trim().toUpperCase().replace(/[^A-Z0-9_-]/g, ''),
  code: ''
})

const phoneDigits = computed(() => form.phone.replace(/\D/g, '').slice(0, 10))
const normalizedPhone = computed(() => (phoneDigits.value.length === 10 ? `+1${phoneDigits.value}` : ''))
const phoneQuery = computed(() => (normalizedPhone.value ? { phone: normalizedPhone.value } : {}))
const maskedPhone = computed(() => {
  const value = normalizedPhone.value
  if (value.length < 5) return value
  return `${value.slice(0, 3)}${'*'.repeat(Math.max(value.length - 5, 3))}${value.slice(-2)}`
})
const headerCopy = computed(() => {
  if (step.value === 'code') {
    return {
      title: 'Verify Your Phone',
      subtitle: 'Enter the code we sent to continue.'
    }
  }

  if (step.value === 'details') {
    return {
      title: 'Finish Your Account',
      subtitle: 'Choose a username and add a join code if you have one.'
    }
  }

  return {
    title: 'Create Your Account',
    subtitle: 'Start with your phone number.'
  }
})

function formatPhoneFromQuery(value) {
  const digits = String(value || '').replace(/\D/g, '').replace(/^1/, '').slice(0, 10)

  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
}

const formatPhoneInput = () => {
  form.phone = formatPhoneFromQuery(form.phone)
  errors.phone = ''
  existingAccountDetected.value = false
}

const formatUsername = () => {
  form.username = form.username.toLowerCase().replace(/[^a-z0-9_]/g, '')
  errors.username = ''
}

const formatJoinCode = () => {
  form.joinCode = form.joinCode.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '')
  errors.joinCode = ''
}

const toggleJoinCode = () => {
  showJoinCode.value = !showJoinCode.value

  if (!showJoinCode.value) {
    form.joinCode = ''
    errors.joinCode = ''
  }
}

const validatePhone = () => {
  errors.phone = ''

  if (!phoneDigits.value) {
    errors.phone = 'Phone number is required.'
  } else if (phoneDigits.value.length !== 10) {
    errors.phone = 'Enter a 10-digit U.S. or Canada phone number.'
  } else if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(phoneDigits.value)) {
    errors.phone = 'Enter a valid U.S. or Canada phone number.'
  }

  return !errors.phone
}

const validateDetails = () => {
  errors.username = ''
  errors.joinCode = ''

  if (!form.username.trim()) {
    errors.username = 'Username is required.'
  } else if (!/^[a-z0-9_]{3,24}$/.test(form.username)) {
    errors.username = 'Use 3-24 letters, numbers, or underscores.'
  }

  if (showJoinCode.value && form.joinCode && !/^[A-Z0-9_-]{3,32}$/.test(form.joinCode)) {
    errors.joinCode = 'Join codes can use letters, numbers, hyphens, or underscores.'
  }

  return !errors.username && !errors.joinCode
}

const validateCode = () => {
  errors.code = ''

  if (!form.code.trim()) {
    errors.code = 'Verification code is required.'
  } else if (!/^\d{4,8}$/.test(form.code.trim())) {
    errors.code = 'Enter the numeric code from your SMS.'
  }

  return !errors.code
}

const joinCode = computed(() => (showJoinCode.value && form.joinCode ? form.joinCode : ''))

const sendSignupCode = async () => {
  if (!validatePhone()) return

  if (!hasSupabaseEnv) {
    errors.phone = 'Supabase is not configured for this local app. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, then restart the dev server.'
    return
  }

  loading.value = true
  existingAccountDetected.value = false

  try {
    const { error } = await auth.sendSignupPhoneOtp(normalizedPhone.value)

    if (error) {
      throw error
    }

    step.value = 'code'
  } catch (error) {
    console.error('Phone signup error:', error)
    errors.phone = formatAuthError(error, 'We could not send a code to that phone number.')
  } finally {
    loading.value = false
  }
}

const stopExistingAccountRegistration = async () => {
  await auth.signOut()
  verifiedUser.value = null
  form.code = ''
  existingAccountDetected.value = true
  step.value = 'phone'
}

const verifySignupCode = async () => {
  if (!validateCode()) return

  loading.value = true

  try {
    const { data, error } = await auth.verifyPhoneOtp(normalizedPhone.value, form.code.trim())

    if (error) {
      throw error
    }

    const currentUser = data?.user || null
    verifiedUser.value = currentUser

    const { data: existingProfile, error: profileError } = await userProfiles.touchLogin(currentUser?.id)

    if (profileError) {
      throw profileError
    }

    if (existingProfile?.username) {
      await stopExistingAccountRegistration()
      return
    }

    if (currentUser?.user_metadata?.username) {
      await stopExistingAccountRegistration()
      return
    }

    step.value = 'details'
  } catch (error) {
    console.error('Phone signup verify error:', error)
    errors.code = formatAuthError(error, 'That code did not work. Check the SMS and try again.')
  } finally {
    loading.value = false
  }
}

const completeRegistration = async () => {
  if (!validateDetails()) return

  loading.value = true

  try {
    let currentUser = verifiedUser.value

    if (!currentUser) {
      const { user, error } = await auth.getUser()

      if (error) {
        throw error
      }

      currentUser = user
    }

    if (!currentUser?.id) {
      throw new Error('No authenticated user found.')
    }

    const { data: existingProfile, error: profileError } = await userProfiles.fetch(currentUser.id)

    if (profileError) {
      throw profileError
    }

    if (existingProfile?.username) {
      await stopExistingAccountRegistration()
      return
    }

    const { error } = await userProfiles.complete({
      userId: currentUser.id,
      phoneNumber: currentUser.phone || normalizedPhone.value,
      username: form.username,
      joinCode: joinCode.value
    })

    if (error) {
      throw error
    }

    try {
      await auth.updateUserData({
        ...(currentUser.user_metadata || {}),
        username: form.username,
        ...(joinCode.value ? { join_code: joinCode.value } : {})
      })
    } catch (metadataError) {
      console.warn('Non-fatal metadata sync error:', metadataError)
    }

    router.push('/pro-dashboard')
  } catch (error) {
    console.error('Registration completion error:', error)
    errors.username = formatAuthError(error, 'We could not finish your account setup.')
  } finally {
    loading.value = false
  }
}

const formatAuthError = (error, fallback) => {
  const message = error?.message || ''

  if (error?.code === '23505' || message.toLowerCase().includes('duplicate key')) {
    return 'That username is already taken.'
  }

  if (!message) return fallback
  return `${fallback} Supabase returned: ${message}`
}
</script>

<style scoped>
.register-page {
  min-height: calc(100vh - 220px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1rem, 3vw, 2.5rem) 1rem 3rem;
}

.register-shell {
  width: min(420px, 100%);
}

.register-header {
  text-align: center;
  margin-bottom: 1rem;
}

.register-header h1 {
  color: var(--text-primary);
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 1.04;
  margin-bottom: 0.6rem;
}

.register-header p {
  color: var(--text-secondary);
}

.eyebrow {
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.register-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-md);
  padding: clamp(1.25rem, 3vw, 1.75rem);
}

.form-detail {
  margin-bottom: 1rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.45;
  text-align: center;
}

.form-group {
  margin-bottom: 1rem;
}

.form-label span {
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 700;
}

.form-input {
  min-height: 44px;
}

.form-input.error {
  border-color: rgba(252, 165, 165, 0.82);
}

.phone-input-wrap {
  min-height: 44px;
  display: flex;
  align-items: center;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.phone-input-wrap:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(91, 124, 255, 0.14);
}

.phone-input-wrap.error {
  border-color: rgba(252, 165, 165, 0.82);
}

.country-prefix {
  flex: 0 0 auto;
  color: var(--accent);
  font-weight: 800;
  padding: 0 0.85rem;
  border-right: 1px solid var(--border);
}

.phone-input {
  width: 100%;
  border: 0;
  outline: 0;
  padding: 0.75rem 0.9rem;
  color: var(--text-primary);
  font: inherit;
  background: transparent;
}

.form-help {
  margin-top: 0.4rem;
  color: var(--text-muted);
  font-size: 0.84rem;
}

.error-message {
  display: block;
  margin-top: 0.45rem;
  color: #b91c1c;
  font-size: 0.84rem;
  font-weight: 700;
}

.account-exists-message {
  margin-top: 0.55rem;
  color: #b91c1c;
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1.35;
}

.account-exists-message .link {
  color: #991b1b;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.register-toggle-note {
  margin: -0.15rem 0 1rem;
  color: var(--text-muted);
  font-size: 0.86rem;
  text-align: center;
}

.inline-toggle {
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  text-decoration: none;
}

.inline-toggle:hover {
  color: var(--accent);
}

.join-code-panel {
  padding: 0.85rem;
  border: 1px solid rgba(91, 124, 255, 0.18);
  border-radius: 10px;
  background: rgba(91, 124, 255, 0.06);
}

.register-footer {
  margin: 1rem 0 0;
  color: var(--text-muted);
  font-size: 0.86rem;
  text-align: center;
}

.link {
  color: var(--text-primary);
  font-weight: 800;
  text-decoration: none;
}

.link:hover {
  color: var(--accent);
}

.code-header {
  text-align: center;
  margin-bottom: 1rem;
}

.code-header h2 {
  color: var(--text-primary);
  font-size: 1rem;
  margin-bottom: 0.35rem;
}

.code-input {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.register-actions {
  display: flex;
  gap: 0.7rem;
  flex-wrap: wrap;
}

.register-actions .btn {
  flex: 1 1 140px;
}

.btn {
  min-height: 44px;
}

.spinner {
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid #fff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0); }
  100% { transform: rotate(360deg); }
}
</style>
